import React, { Fragment, useEffect, useState } from 'react'
import Select from 'react-select'
import { API_URL } from './utils/Constants/URLS'
import '../css/common.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheck,
    faPen,
    faTrashCan,
    faXmark
} from '@fortawesome/free-solid-svg-icons'
import { Table } from 'reactstrap'

const FocLineItems = ({ focLeadId, focLeadDetails, onDataUpdate }) => {
    const token = localStorage.getItem('token')
    const [crossSellData, setCrossSellData] = useState([])
    const [prodStatus, setProdStatus] = useState('')
    const [prodIndex, setProdIndex] = useState(-1)
    const [businessLine, setBusinessLine] = useState('')
    const [businessLineList, setBusinessLineList] = useState([])
    const [productLine, setProductLine] = useState('')
    const [modelId, setModelId] = useState('')
    const [isEdit, setIsEdit] = useState(null)
    const [productLineList, setProductLineList] = useState([])
    const [models, setModel] = useState([])
    const [lp, setLp] = useState(0)
    const [quantity, setQuantity] = useState(0)
    const [committedDeliveryDate, setCommittedDeliveryDate] = useState(null)
    const [standardLeadTime, setStandardLeadTime] = useState('')
    const [stdDuration, setStdDuration] = useState('')
    const [editedCommitedDeliveryDate, setEditedDeliveryDate] = useState('')
    const [editedStandardLeadTime, setEditedStandardLeadTime] = useState('')
    const [showSuccess, setShowSuccess] = useState(false)

    // Fetch Business Lines
    useEffect(() => {
        const fetchBusinessLines = async () => {
            try {
                const res = await fetch(
                    `${API_URL}business-lines?pagination=false`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'Application/json',
                            Authorization: `Bearer ${token}`
                        }
                    }
                )
                const result = await res.json()
                setBusinessLineList(
                    result.data.docs.map(business => ({
                        value: business.id,
                        label: business.name
                    }))
                )
            } catch (e) {
                console.log(e)
            }
        }
        fetchBusinessLines()
    }, [token])

    // Fetch Product Lines based on Business Line
    useEffect(() => {
        const fetchProductLines = async () => {
            try {
                const res = await fetch(
                    `${API_URL}product-lines?businessLine=${businessLine}&pagination=false`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'Application/json',
                            Authorization: `Bearer ${token}`
                        }
                    }
                )
                const result = await res.json()
                setProductLineList(
                    result.data.docs.map(product => ({
                        value: product.id,
                        label: product.productLineName
                    }))
                )
            } catch (e) {
                console.log(e)
                setProductLineList([])
            }
        }
        if (businessLine !== '') {
            fetchProductLines()
            setProductLine('')
            setModelId('')
            setModel([])
        }
    }, [businessLine, token])

    // Fetch Models based on Product Line
    useEffect(() => {
        const fetchModels = async () => {
            try {
                const res = await fetch(
                    `${API_URL}models?productLine=${productLine}&pagination=false`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'Application/json',
                            Authorization: `Bearer ${token}`
                        }
                    }
                )
                const result = await res.json()
                setModel(
                    result.data.docs.map(model => ({
                        value: model.id,
                        label: `${model.modelName} | ${model.partCode}`,
                        lp: model.listPrice
                    }))
                )
            } catch (e) {
                console.log(e)
                setModel([])
            }
        }
        if (productLine !== '') {
            fetchModels()
            setModelId('')
        }
    }, [productLine, token])

    // Fetch Cross-Sell Data
    useEffect(() => {
        const fetchCrossSellData = async () => {
            try {
                const res = await fetch(`${API_URL}foc/cross-sell?leadId=${focLeadId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'Application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const result = await res.json()
                setCrossSellData(result.data || [])
            } catch (e) {
                console.log(e)
            }
        }
        if (focLeadId) {
            fetchCrossSellData()
        }
    }, [focLeadId, token])

    const addCrossSell = async e => {
        e.preventDefault()
        try {
            const res = await fetch(`${API_URL}foc/cross-sell`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    leadId: focLeadId,
                    businessLine,
                    productLine,
                    model: modelId,
                    quantity,
                    value: 0,
                    listPrice: lp,
                    discount: 0,
                    rate: 0,
                    standardLeadTime: `${standardLeadTime} ${stdDuration}`,
                    committedDeliveryDate
                })
            })
            const result = await res.json()
            if (result.error === false) {
                setShowSuccess(true)
                setTimeout(() => setShowSuccess(false), 3000)
                // Refresh data
                const refreshRes = await fetch(`${API_URL}foc/cross-sell?leadId=${focLeadId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'Application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const refreshResult = await refreshRes.json()
                setCrossSellData(refreshResult.data || [])

                // Notify parent to refresh FOC lead details
                if (onDataUpdate) {
                    onDataUpdate()
                }

                // Reset form
                setBusinessLine('')
                setProductLine('')
                setModelId('')
                setQuantity(0)
                setLp(0)
                setStandardLeadTime('')
                setStdDuration('')
                setCommittedDeliveryDate(null)
            }
        } catch (e) {
            console.log(e)
        }
    }

    const handleQuantityChange = async (data, change, index) => {
        const newQuantity = data.quantity + change
        if (newQuantity < 1) return

        try {
            await fetch(`${API_URL}foc/cross-sell/${data.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ quantity: newQuantity })
            })
            // Update local state
            const updatedData = [...crossSellData]
            updatedData[index].quantity = newQuantity
            setCrossSellData(updatedData)
        } catch (e) {
            console.log(e)
        }
    }

    const handleEdit = (index, data) => {
        setIsEdit(index)
        setEditedStandardLeadTime(data.standardLeadTime)
        setEditedDeliveryDate(data.committedDeliveryDate?.split('T')[0] || '')
    }

    const handleSaveEdit = async (data, index) => {
        try {
            await fetch(`${API_URL}foc/cross-sell/${data.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    standardLeadTime: editedStandardLeadTime,
                    committedDeliveryDate: editedCommitedDeliveryDate
                })
            })
            // Update local state
            const updatedData = [...crossSellData]
            updatedData[index].standardLeadTime = editedStandardLeadTime
            updatedData[index].committedDeliveryDate = editedCommitedDeliveryDate
            setCrossSellData(updatedData)
            setIsEdit(null)
        } catch (e) {
            console.log(e)
        }
    }

    const handleDelete = async (data, index) => {
        try {
            const res = await fetch(`${API_URL}foc/cross-sell/${data.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            const result = await res.json()
            if (result.error === false) {
                // Remove from local state
                const updatedData = crossSellData.filter((_, idx) => idx !== index)
                setCrossSellData(updatedData)

                // Notify parent to refresh FOC lead details
                if (onDataUpdate) {
                    onDataUpdate()
                }
            } else {
                alert('Something went wrong')
            }
        } catch (e) {
            console.log(e)
        }
    }

    // Check if buying platform is completed (submitted and marked as done)
    const isBuyingPlatformComplete = () => {
        return focLeadDetails?.buyingPlatform?.status && focLeadDetails.buyingPlatform.status !== 'pending'
    }

    // Disable form when buying platform IS completed (submitted)
    const isFormDisabled = isBuyingPlatformComplete()

    return (
        <Fragment>
            <div className='p-3'>
                <h5 className='mb-3'>Add FOC Line Items</h5>

                {/* Warning Message when Buying Platform is completed */}
                {isFormDisabled && (
                    <div className='alert alert-warning' role='alert'>
                        <strong>Note:</strong> Line items cannot be added after the Buying Platform has been submitted and marked as done.
                    </div>
                )}

                {/* Add Product Form */}
                <form onSubmit={addCrossSell}>
                    {/* Row 1: Business Line, Product Line, Model */}
                    <div className='row mb-3'>
                        <div className='col-lg-4 col-md-6 col-sm-12 mb-2'>
                            <label className='form-label'>
                                Business Line <span className='text-danger'>*</span>
                            </label>
                            <Select
                                value={businessLineList.find(b => b.value === businessLine)}
                                onChange={e => setBusinessLine(e.value)}
                                options={businessLineList}
                                placeholder='Select Business Line'
                                isDisabled={isFormDisabled}
                            />
                        </div>
                        <div className='col-lg-4 col-md-6 col-sm-12 mb-2'>
                            <label className='form-label'>
                                Product Line <span className='text-danger'>*</span>
                            </label>
                            <Select
                                value={productLineList.find(p => p.value === productLine)}
                                onChange={e => setProductLine(e.value)}
                                options={productLineList}
                                placeholder='Select Product Line'
                                isDisabled={!businessLine || isFormDisabled}
                            />
                        </div>
                        <div className='col-lg-4 col-md-6 col-sm-12 mb-2'>
                            <label className='form-label'>
                                Model <span className='text-danger'>*</span>
                            </label>
                            <Select
                                value={models.find(m => m.value === modelId)}
                                onChange={e => {
                                    setModelId(e.value)
                                    setLp(e.lp)
                                }}
                                options={models}
                                placeholder='Select Model'
                                isDisabled={!productLine || isFormDisabled}
                            />
                        </div>
                    </div>

                    {/* Row 2: QTY, STD Lead Time, Duration, Committed Delivery Date */}
                    <div className='row mb-3'>
                        <div className='col-lg-2 col-md-3 col-sm-6 mb-2'>
                            <label className='form-label'>
                                QTY <span className='text-danger'>*</span>
                            </label>
                            <input
                                type='number'
                                value={quantity}
                                placeholder='Enter Qty'
                                onChange={e => setQuantity(e.target.value)}
                                onWheel={e => e.target.blur()}
                                className='form-control'
                                min='1'
                                disabled={isFormDisabled}
                            />
                        </div>
                        <div className='col-lg-2 col-md-3 col-sm-6 mb-2'>
                            <label className='form-label'>
                                STD. Lead Time <span className='text-danger'>*</span>
                            </label>
                            <input
                                type='number'
                                value={standardLeadTime}
                                placeholder='Enter Value'
                                onChange={e => setStandardLeadTime(e.target.value)}
                                className='form-control'
                                disabled={isFormDisabled}
                            />
                        </div>
                        <div className='col-lg-2 col-md-3 col-sm-6 mb-2'>
                            <label className='form-label'>
                                Duration <span className='text-danger'>*</span>
                            </label>
                            <select
                                value={stdDuration}
                                onChange={e => setStdDuration(e.target.value)}
                                className='form-select'
                                disabled={isFormDisabled}
                            >
                                <option value=''>Select</option>
                                <option value='Days'>Days</option>
                                <option value='Weeks'>Weeks</option>
                                <option value='Months'>Months</option>
                            </select>
                        </div>
                        <div className='col-lg-4 col-md-6 col-sm-12 mb-2'>
                            <label className='form-label'>
                                Committed Delivery Date <span className='text-danger'>*</span>
                            </label>
                            <input
                                className='form-control'
                                type='date'
                                onClick={e => e.target.showPicker && e.target.showPicker()}
                                value={committedDeliveryDate || ''}
                                onChange={e => setCommittedDeliveryDate(e.target.value)}
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>

                    {/* Row 3: Add Product Button */}
                    <div className='row mb-3'>
                        <div className='col-12'>
                            <button type='submit' className='btn add-btn text-white' disabled={isFormDisabled}>
                                Add Product
                            </button>
                        </div>
                    </div>

                    {showSuccess && (
                        <div className='row'>
                            <div className='col-12 p-3 fade-out bg-green-100 text-green-600'>
                                Product Added to the list...
                            </div>
                        </div>
                    )}
                </form>

                {/* Products Table */}
                <div className='table-responsive mt-4'>
                    <Table>
                        <thead className='table-info'>
                            <tr>
                                <th>Product Line</th>
                                <th>Model</th>
                                <th>QTY</th>
                                <th>STD. Lead Time</th>
                                <th>Committed Delivery Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {crossSellData && crossSellData.filter(item => item.status !== 'deleted').length > 0 ? (
                                crossSellData.filter(item => item.status !== 'deleted').map((data, index) => (
                                    <tr key={data.id || index}>
                                        <td>
                                            <span className={data.status === 'added' ? 'enable' : 'disabled'}>
                                                {data.productLine?.productLineName || data.productLine || '-'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={data.status === 'added' ? 'enable' : 'disabled'}>
                                                {data.model?.modelName || '-'} | {data.model?.partCode || '-'}
                                            </span>
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '6px',
                                                    padding: '2px 8px',
                                                    backgroundColor: '#f9f9f9',
                                                    gap: '10px'
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(data, -1, index)}
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: 'transparent',
                                                        fontSize: '16px',
                                                        cursor: 'pointer',
                                                        color: '#ff6600'
                                                    }}
                                                    disabled={data.status === 'deleted'}
                                                >
                                                    −
                                                </button>
                                                <span
                                                    className={data.status === 'added' ? 'enable' : 'disabled'}
                                                    style={{ minWidth: '20px', textAlign: 'center' }}
                                                >
                                                    {data.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(data, 1, index)}
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: 'transparent',
                                                        fontSize: '16px',
                                                        cursor: 'pointer',
                                                        color: '#28a745'
                                                    }}
                                                    disabled={data.status === 'deleted'}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </td>

                                        {isEdit === index ? (
                                            <>
                                                <td>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        value={editedStandardLeadTime}
                                                        onChange={e => setEditedStandardLeadTime(e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className='form-control'
                                                        type='date'
                                                        value={editedCommitedDeliveryDate}
                                                        onChange={e => setEditedDeliveryDate(e.target.value)}
                                                    />
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>
                                                    <span className={data.status === 'added' ? 'enable' : 'disabled'}>
                                                        {data.standardLeadTime}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={data.status === 'added' ? 'enable' : 'disabled'}>
                                                        {data.committedDeliveryDate?.split('T')[0]}
                                                    </span>
                                                </td>
                                            </>
                                        )}

                                        <td>
                                            {isEdit === index ? (
                                                <>
                                                    <button
                                                        className='btn btn-sm btn-success me-2'
                                                        onClick={() => handleSaveEdit(data, index)}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </button>
                                                    <button
                                                        className='btn btn-sm btn-secondary'
                                                        onClick={() => setIsEdit(null)}
                                                    >
                                                        <FontAwesomeIcon icon={faXmark} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        className='btn btn-sm btn-primary me-2'
                                                        onClick={() => handleEdit(index, data)}
                                                        disabled={data.status === 'deleted'}
                                                    >
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </button>
                                                    <button
                                                        className='btn btn-sm btn-danger'
                                                        onClick={() => handleDelete(data, index)}
                                                        disabled={data.status === 'deleted'}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashCan} />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan='6' className='text-center'>
                                        No products added yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </Fragment>
    )
}

export default FocLineItems
